import { useCallback, useEffect, useMemo, useState } from 'react'

import { CAMPAIGN_CHANNELS } from '#/entities/campaign'
import type { CampaignChannel } from '#/entities/campaign'
import { useDistributionUsers } from '#/entities/shipment'
import { useMyLines } from '#/entities/telephony'
import { smsBodyFromHtml } from '#/shared/lib/sms-body'
import { StepTaskType } from '#/shared/api/generated/communication/models'
import { useCampaignTemplatesApiV1CampaignsTemplatesGet } from '#/shared/api/generated/communication/theCargoCommunication'
import { formatPhone } from '#/shared/lib/format'
import type { SelectOption } from '#/shared/ui/select'

import {
  MINUTES_PER_DAY,
  campaignStepWaitOptions,
  customDaysToMinutes,
} from './use-campaign-add-step'
import type { CampaignStepCondition } from './use-campaign-add-step'

/** How an email step joins the conversation. */
export const CAMPAIGN_SEND_AS = {
  thread: 'thread',
  newSubject: 'newSubject',
} as const

export type CampaignSendAs =
  (typeof CAMPAIGN_SEND_AS)[keyof typeof CAMPAIGN_SEND_AS]

export const CAMPAIGN_SEND_RATES = {
  natural: 'natural',
  steady: 'steady',
  fast: 'fast',
} as const

export type CampaignSendRate =
  (typeof CAMPAIGN_SEND_RATES)[keyof typeof CAMPAIGN_SEND_RATES]

/** SMS is throttled harder than email — carriers, not our own pacing. */
export const CAMPAIGN_SMS_RATES: readonly CampaignSendRate[] = [
  CAMPAIGN_SEND_RATES.natural,
  CAMPAIGN_SEND_RATES.steady,
]

export const CAMPAIGN_EMAIL_RATES: readonly CampaignSendRate[] = [
  CAMPAIGN_SEND_RATES.natural,
  CAMPAIGN_SEND_RATES.steady,
  CAMPAIGN_SEND_RATES.fast,
]

export const CAMPAIGN_STARTS = {
  now: 'now',
  schedule: 'schedule',
} as const

export type CampaignStart =
  (typeof CAMPAIGN_STARTS)[keyof typeof CAMPAIGN_STARTS]

/** Character ceilings the inbox imposes, not us. */
export const CAMPAIGN_SUBJECT_LIMIT = 60
export const CAMPAIGN_PREVIEW_TEXT_LIMIT = 90

/** Default hour for a scheduled step, in the picker's 24h `HH:mm`. */
const DEFAULT_START_TIME = '09:00'

/** What the composer hands over on save — the step as it will be created. */
export interface CampaignStepDraft {
  channel: CampaignChannel
  body: string
  /** The email step's subject line, and the task step's title — one field,
   *  because that is how the service stores both. */
  subject: string
  previewText: string
  sendAs: CampaignSendAs
  rate: CampaignSendRate
  fromNumber: string
  /** ISO instant to hold this step until, or null to run it on its delay. */
  startsAt: string | null
  taskType: StepTaskType
  /** '' means the quote's own agent, which the service reads as null. */
  assigneeUserId: string
  /** Days after the step runs, as typed. '' leaves the task undated. */
  dueOffsetDays: string
  /** Wait after the previous step. Null when the add-step picker owns it. */
  delayMinutes: number | null
  /** Its "only if" conditions — empty is "always send". Null when the picker owns them. */
  conditions: CampaignStepCondition[] | null
}

/**
 * The picker's date and `HH:mm` as one instant. Built in local time on
 * purpose: an agent scheduling "9:00" means nine where they are, and the
 * field is a plain `date-time` the service stores as sent.
 */
function toStartsAt(date: Date | undefined, time: string): string | null {
  if (date == null) return null
  const [hour, minute] = time.split(':').map(Number)
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null
  const at = new Date(date)
  at.setHours(hour, minute, 0, 0)
  return at.toISOString()
}

/** An existing step's content, to open the composer on rather than a blank one. */
export interface CampaignComposerSeed {
  body: string
  subject: string
  previewText: string
  sendAs: CampaignSendAs
  rate: CampaignSendRate
  fromNumber: string
  /** The step's own hold, as the service stores it. Null = sends on its delay. */
  startsAt: string | null
  taskType: StepTaskType
  assigneeUserId: string
  dueOffsetDays: string
  /** The step's stored wait. Null on a new step — the picker already asked. */
  delayMinutes: number | null
  /** The step's stored conditions. Null on a new step, like the wait. */
  conditions: CampaignStepCondition[] | null
}

const BLANK_SEED: CampaignComposerSeed = {
  body: '',
  subject: '',
  previewText: '',
  sendAs: CAMPAIGN_SEND_AS.thread,
  rate: CAMPAIGN_SEND_RATES.natural,
  fromNumber: '',
  startsAt: null,
  taskType: StepTaskType.call,
  assigneeUserId: '',
  dueOffsetDays: '',
  delayMinutes: null,
  conditions: null,
}

/** The kinds of work a task step can queue, in the order the chips read. */
export const CAMPAIGN_TASK_TYPE_ORDER: readonly StepTaskType[] = [
  StepTaskType.call,
  StepTaskType.email,
  StepTaskType.todo,
]

/** The stored hold, split back into the two controls that produced it. */
function fromStartsAt(startsAt: string | null): {
  start: CampaignStart
  date: Date | undefined
  time: string
} {
  if (startsAt == null) {
    return {
      start: CAMPAIGN_STARTS.now,
      date: undefined,
      time: DEFAULT_START_TIME,
    }
  }
  const at = new Date(startsAt)
  if (Number.isNaN(at.getTime())) {
    return {
      start: CAMPAIGN_STARTS.now,
      date: undefined,
      time: DEFAULT_START_TIME,
    }
  }
  const time = `${String(at.getHours()).padStart(2, '0')}:${String(
    at.getMinutes(),
  ).padStart(2, '0')}`
  return { start: CAMPAIGN_STARTS.schedule, date: at, time }
}

interface UseCampaignComposerOptions {
  channel: CampaignChannel
  onCancel: () => void
  /** Read once, on mount — editing a step opens a composer of its own. */
  seed?: CampaignComposerSeed
}

/**
 * The step composer's own state.
 *
 * Everything here is what the step *will* be, not what it is — the sequence
 * only learns about it on save. Counters (characters, segments, subject length)
 * are derived on the fly because they describe the text in the box rather than
 * anything the backend computed. Templates come from `GET /campaigns/templates`
 * for the channel; picking one fills the box, it does not bind the step to it.
 */
/**
 * The three fields only a task step has, and the list of people one can be
 * assigned to. Separate because only a task uses any of it: an email or SMS
 * composer must not ask the server who is assignable to fill a control it
 * never renders.
 */
function useTaskFields(from: CampaignComposerSeed, isTask: boolean) {
  const [taskType, setTaskType] = useState<StepTaskType>(from.taskType)
  const [assigneeUserId, setAssigneeUserId] = useState(from.assigneeUserId)
  const [dueOffsetDays, setDueOffsetDays] = useState(from.dueOffsetDays)
  const { users: assignableUsers, isLoading: assigneesLoading } =
    useDistributionUsers(isTask)
  return {
    taskType,
    setTaskType,
    assigneeUserId,
    setAssigneeUserId,
    dueOffsetDays,
    setDueOffsetDays,
    assignableUsers,
    assigneesLoading,
  }
}

/**
 * The step's wait, editable only when the composer opened on a stored step.
 * A new step's wait was chosen on the picker before the composer opened, so
 * there it stays null and the row does not render.
 */
function useWaitField(
  stored: number | null,
  storedConditions: CampaignStepCondition[] | null,
) {
  const [picked, setPicked] = useState(stored)
  const waitOptions = useMemo(
    () => (stored == null ? [] : campaignStepWaitOptions(stored)),
    [stored],
  )
  // Null while a chip is picked; the typed day count once Custom is.
  const [customDays, setCustomDays] = useState<string | null>(null)
  const setDelayMinutes = useCallback((minutes: number) => {
    setCustomDays(null)
    setPicked(minutes)
  }, [])
  // Opens on the current wait when it is a whole number of days, so switching
  // to Custom starts from what the step already does.
  const pickCustom = useCallback(() => {
    setCustomDays(
      picked != null && picked > 0 && picked % MINUTES_PER_DAY === 0
        ? String(picked / MINUTES_PER_DAY)
        : '',
    )
  }, [picked])
  const delayMinutes =
    customDays == null ? picked : customDaysToMinutes(customDays)
  // "Always send" is the empty set, exactly as on the add picker.
  const [conditions, setConditions] = useState(storedConditions)
  const toggleCondition = useCallback((condition: CampaignStepCondition) => {
    setConditions((current) =>
      current?.includes(condition)
        ? current.filter((item) => item !== condition)
        : [...(current ?? []), condition],
    )
  }, [])
  const setAlwaysSend = useCallback(() => setConditions([]), [])
  return {
    delayMinutes,
    setDelayMinutes,
    waitOptions,
    customDays,
    setCustomDays,
    pickCustom,
    /** Custom is picked and its day count is not a whole 1–365. */
    customDaysInvalid: customDays != null && delayMinutes == null,
    conditions,
    toggleCondition,
    setAlwaysSend,
  }
}

export function useCampaignComposer({
  channel,
  onCancel,
  seed,
}: UseCampaignComposerOptions) {
  const isSms = channel === CAMPAIGN_CHANNELS.sms
  const isTask = channel === CAMPAIGN_CHANNELS.task
  const isEmail = channel === CAMPAIGN_CHANNELS.email
  const from = seed ?? BLANK_SEED
  const [body, setBody] = useState(from.body)
  const [sendAs, setSendAs] = useState<CampaignSendAs>(from.sendAs)
  const [subject, setSubject] = useState(from.subject)
  const [previewText, setPreviewText] = useState(from.previewText)
  const [template, setTemplateId] = useState('')
  const [fromNumber, setFromNumber] = useState(from.fromNumber)
  const [rate, setRate] = useState<CampaignSendRate>(from.rate)
  // An edit opens on the step's own hold, so the control states what is
  // actually stored rather than resetting every step to "Now".
  const seeded = fromStartsAt(from.startsAt)
  const [start, setStart] = useState<CampaignStart>(seeded.start)
  const [startDate, setStartDate] = useState<Date | undefined>(seeded.date)
  const [startTime, setStartTime] = useState(seeded.time)
  const task = useTaskFields(from, isTask)
  const wait = useWaitField(from.delayMinutes, from.conditions)

  // The composer mounts only while a step is being written, so both fetches
  // fire on that interaction and never for a sequence nobody is editing.
  const templatesQuery = useCampaignTemplatesApiV1CampaignsTemplatesGet({
    channel,
  })
  const templates = useMemo(
    () => templatesQuery.data?.data ?? [],
    [templatesQuery.data],
  )
  const templateOptions: SelectOption[] = useMemo(
    () => templates.map((item) => ({ value: item.id, label: item.name })),
    [templates],
  )
  const setTemplate = useCallback(
    (id: string) => {
      setTemplateId(id)
      const picked = templates.find((item) => item.id === id)
      if (picked == null) return
      // Template bodies are HTML. An SMS step carries plain text, so the
      // markup is flattened on the way in — pasted tags would otherwise show
      // in the box and be priced as characters by the segment counter.
      setBody(isSms ? smsBodyFromHtml(picked.body) : picked.body)
      if (picked.subject != null) setSubject(picked.subject)
    },
    [templates, isSms],
  )

  const { lines } = useMyLines({ enabled: isSms })
  const lineOptions: SelectOption[] = useMemo(
    () =>
      lines
        .filter((line) => line.outbound_sms)
        .map((line) => ({
          value: line.phone_number,
          label: line.label ?? formatPhone(line.phone_number),
        })),
    [lines],
  )
  useEffect(() => {
    const first = lineOptions.at(0)
    if (fromNumber === '' && first != null) setFromNumber(first.value)
  }, [fromNumber, lineOptions])

  // Picking Schedule with no date would save as unscheduled without saying so.
  // Seeding today makes the control speak for itself; the step's own delay
  // still governs when both are in play.
  const onStartChange = useCallback((next: CampaignStart) => {
    setStart(next)
    if (next === CAMPAIGN_STARTS.schedule) {
      setStartDate((current) => current ?? new Date())
    }
  }, [])

  const cancel = useCallback(() => onCancel(), [onCancel])

  return {
    isSms,
    body,
    setBody,
    sendAs,
    setSendAs,
    subject,
    setSubject,
    previewText,
    setPreviewText,
    template,
    setTemplate,
    templateOptions,
    fromNumber,
    setFromNumber,
    lineOptions,
    rate,
    setRate,
    start,
    setStart: onStartChange,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    cancel,
    // What makes the step savable at all: a message needs words, a task needs
    // a title. The service enforces the same split with a 422 on the field.
    //
    // An email that opens a new subject needs one written — the service
    // answers "An email step needs a subject", and the field is right there
    // above the button. A threaded step is exempt because it has no subject to
    // fill: it inherits step 1's, the composer shows that row locked, and
    // `channelFields` sends no subject for it at all.
    saveBlocked:
      wait.customDaysInvalid ||
      (isTask
        ? subject.trim() === ''
        : body.trim() === '' ||
          (isEmail &&
            sendAs === CAMPAIGN_SEND_AS.newSubject &&
            subject.trim() === '')),
    isTask,
    ...task,
    ...wait,
    rates: isSms ? CAMPAIGN_SMS_RATES : CAMPAIGN_EMAIL_RATES,
    draft: {
      channel,
      body,
      subject,
      previewText,
      sendAs,
      rate,
      fromNumber,
      startsAt:
        start === CAMPAIGN_STARTS.schedule
          ? toStartsAt(startDate, startTime)
          : null,
      taskType: task.taskType,
      assigneeUserId: task.assigneeUserId,
      dueOffsetDays: task.dueOffsetDays,
      delayMinutes: wait.delayMinutes,
      conditions: wait.conditions,
    } satisfies CampaignStepDraft,
  }
}

export type UseCampaignComposerResult = ReturnType<typeof useCampaignComposer>
